import { type FC, useState } from "react";
import { Button, Stack, TextField, Tooltip } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { ConfirmDialog } from "@/shared/components";
import { useAbility } from "@/partner/shared/lib";
import { useCreateRefundMutation } from "@/partner/entities/refunds";
import { PaymentProviderType } from "@/types/graphql/partner.generated";
import { PAYMENT_PROVIDER_LABEL_KEY } from "@/partner/shared/utils/paymentProvider";

interface RefundButtonProps {
  paymentId: string;
  provider: PaymentProviderType;
  defaultAmount: number;
}

/**
 * Возврат по платежу партнёром (референс админской пары RefundButton +
 * CreateRefundDialog). В отличие от админа поле суммы по умолчанию
 * заполнено полной суммой платежа (бриф 2026-09-19-partner-payments-cabinet),
 * поэтому значение переинициализируется при каждом открытии диалога, а не
 * только при монтировании.
 *
 * CCBill не поддерживает возврат через API (см. RefundService.ts), поэтому
 * кнопка остаётся видимой, но заблокированной, с подсказкой почему. Старые
 * платежи Paynet (без номера заказа ecom-API) бэкенд учитывает как ручной
 * возврат (`manual`): после него партнёр видит предупреждение, что деньги
 * возвращаются руками в кабинете провайдера.
 */
export const RefundButton: FC<RefundButtonProps> = ({
  paymentId,
  provider,
  defaultAmount,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const ability = useAbility();
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(String(defaultAmount));
  const [description, setDescription] = useState("");

  const [createRefund, { loading, error, reset }] = useCreateRefundMutation({
    // Ответ мутации уже несёт payment { status, refunds }, кеш обновляется
    // сам; refetch страхует count/фильтры списков платежей и возвратов.
    refetchQueries: ["PaymentsList", "RefundsList"],
    onCompleted: (data) => {
      if (data.createRefund.manual) {
        enqueueSnackbar(
          t("payments.refund.manualSnackbar", {
            provider: t(PAYMENT_PROVIDER_LABEL_KEY[provider]),
          }),
          { variant: "warning", autoHideDuration: 15000 }
        );
      } else {
        enqueueSnackbar(t("payments.refund.successSnackbar"), {
          variant: "success",
        });
      }
      setOpen(false);
    },
    onError: () => {
      // ошибка уже отображается через ErrorMessage внутри ConfirmDialog
    },
  });

  if (!ability.can("create", "Refund")) return null;

  // Paynet возвраты делает через свой ecom-API, блокируется только CCBill.
  const unsupportedMessage =
    provider === PaymentProviderType.CcbillType
      ? t("payments.refund.unsupportedCcbill")
      : null;

  const handleOpen = () => {
    setAmount(String(defaultAmount));
    setDescription("");
    reset();
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    reset();
  };

  const handleConfirm = () => {
    if (!amount) return;

    void createRefund({
      variables: {
        input: {
          paymentId,
          amount: Number(amount),
          description: description || undefined,
        },
      },
    });
  };

  const button = (
    <Button
      size="small"
      disabled={Boolean(unsupportedMessage)}
      onClick={(event) => {
        event.stopPropagation();
        handleOpen();
      }}
    >
      {t("payments.refund.button")}
    </Button>
  );

  return (
    <>
      {unsupportedMessage ? (
        <Tooltip title={unsupportedMessage}>
          <span>{button}</span>
        </Tooltip>
      ) : (
        button
      )}
      <ConfirmDialog
        open={open}
        onCancel={handleClose}
        onConfirm={handleConfirm}
        confirmTitle={t("payments.refund.dialogTitle")}
        confirmText={t("payments.refund.dialogText")}
        loading={loading}
        error={error}
      >
        <Stack spacing={2}>
          <TextField
            label={t("payments.refund.amountLabel")}
            type="number"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            fullWidth
            autoFocus
          />
          <TextField
            label={t("payments.refund.descriptionLabel")}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            fullWidth
            multiline
            minRows={2}
          />
        </Stack>
      </ConfirmDialog>
    </>
  );
};
